/**
 * AI 壁打ちダイアログ（件名・セクション共用）。
 * ユーザーのフィードバック → Claude 提案 → 採用 or 追加フィードバックのループ。
 */
import { Lightbulb, Send } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

export interface BrainstormResult {
  proposal: string;
  alternatives: string[];
  reasoning: string;
}

interface BrainstormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** 現在の対象（件名本文やセクション本文） */
  currentContent: string;
  /** 壁打ち Callable 呼出（親が差し込む） */
  runBrainstorm: (userFeedback: string) => Promise<BrainstormResult>;
  /** 採用時に親に渡す */
  onAdopt: (newContent: string) => void;
}

export function BrainstormDialog({
  open,
  onOpenChange,
  title,
  currentContent,
  runBrainstorm,
  onAdopt,
}: BrainstormDialogProps) {
  const [feedback, setFeedback] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BrainstormResult | null>(null);

  const handleSubmit = async () => {
    if (!feedback.trim()) return;
    setRunning(true);
    try {
      const r = await runBrainstorm(feedback);
      setResult(r);
    } catch (err) {
      // 親で toast 済なのでここは catch するだけ
      console.error(err);
    } finally {
      setRunning(false);
    }
  };

  const handleAdopt = (content: string) => {
    onAdopt(content);
    onOpenChange(false);
    setFeedback('');
    setResult(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) {
          setFeedback('');
          setResult(null);
        }
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="size-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            現在の内容を元に、改善したい方向を言葉で伝えてください。Claude が提案します。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">現在の内容</Label>
            <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] p-3 text-[12px] whitespace-pre-wrap max-h-32 overflow-y-auto">
              {currentContent || '（空）'}
            </div>
          </div>

          <div>
            <Label htmlFor="brainstorm-feedback">改善したい方向</Label>
            <Textarea
              id="brainstorm-feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={`例:\n・もっとカジュアルなトーンに\n・行動喚起を強めて\n・若年層向けに\n・長すぎるので 3 行に`}
              rows={4}
              disabled={running}
            />
          </div>

          {result && (
            <div className="space-y-3 border-t border-[var(--border)] pt-3">
              <div>
                <Label className="text-xs">🎯 最有力提案</Label>
                <div className="rounded bg-[var(--accent-subtle)] border border-[var(--accent)] p-3 text-[13px] whitespace-pre-wrap">
                  {result.proposal}
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => handleAdopt(result.proposal)}
                >
                  この提案を採用
                </Button>
              </div>

              {result.alternatives.length > 0 && (
                <div>
                  <Label className="text-xs">代替案</Label>
                  {result.alternatives.map((alt, i) => (
                    <div key={i} className="mt-2">
                      <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] p-2 text-[13px] whitespace-pre-wrap">
                        {alt}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-1"
                        onClick={() => handleAdopt(alt)}
                      >
                        代替案 {i + 1} を採用
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {result.reasoning && (
                <p className="text-xs text-[var(--text-muted)] italic">{result.reasoning}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={running}
          >
            閉じる
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={running || !feedback.trim()}
          >
            {running ? (
              <>
                <Spinner className="size-4" />
                思考中...
              </>
            ) : (
              <>
                <Send />
                {result ? '再提案' : 'Claude に提案してもらう'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
